import { useEffect, useReducer, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  WebsiteTemplateServiceError,
  createDraftSaveQueue,
  draftSaveHasWork,
  getStoreWebsiteEditorStateV3,
  invalidateWebsiteTemplateDraft,
  saveStoreWebsiteSlotsV3,
  type DraftSaveQueue,
  type DraftSaveSnapshot,
  type StoreWebsiteDocument,
  type SupabaseClient,
} from '@dukanti/shared';

export interface UseTemplateDraftSaveQueueArgs {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly templateId: string;
  /** The head of the editor state. The queue's own base wins once it exists (PD6). */
  readonly headRevisionId: string;
  /** Accepted for the plan's call shape; the dialog overlays the document itself (PD5). */
  readonly documentSlots?: StoreWebsiteDocument['slots'];
}

export interface UseTemplateDraftSaveQueueResult {
  readonly queue: DraftSaveQueue;
  readonly snapshot: DraftSaveSnapshot;
}

interface RegistryEntry {
  readonly queue: DraftSaveQueue;
  subscribers: number;
  readonly release: () => void;
}

/**
 * One queue per `${storeId}:${templateId}` (design §5.1), shared by every
 * dialog and the toolbar of that template, and kept across route unmounts
 * while it has work: a response that lands after the screen is gone still
 * advances the entry's base. An entry leaves the registry only when nothing
 * subscribes to it, it has no work and it is not paused.
 */
const registry = new Map<string, RegistryEntry>();

const entryKey = (storeId: string, templateId: string): string => `${storeId}:${templateId}`;

function canRetire(entry: RegistryEntry): boolean {
  const snapshot = entry.queue.getSnapshot();
  return (
    entry.subscribers === 0 &&
    !draftSaveHasWork(snapshot) &&
    !snapshot.paused &&
    snapshot.fetching === null
  );
}

function retireIfIdle(key: string, entry: RegistryEntry): void {
  if (registry.get(key) !== entry || !canRetire(entry)) return;
  registry.delete(key);
  entry.release();
  entry.queue.dispose();
}

/** Test seam: whether a queue of this template is alive in the registry. */
export function hasTemplateDraftSaveQueue(storeId: string, templateId: string): boolean {
  return registry.has(entryKey(storeId, templateId));
}

function createEntry(
  key: string,
  args: UseTemplateDraftSaveQueueArgs,
  queryClient: QueryClient,
): RegistryEntry {
  const { client, storeId, templateId } = args;
  const queue = createDraftSaveQueue({
    base: args.headRevisionId,
    save: (payload) =>
      saveStoreWebsiteSlotsV3(client, {
        storeId,
        templateId,
        values: payload.values,
        expectedHeadRevisionId: payload.expectedHeadRevisionId,
        clientMutationId: payload.clientMutationId,
      }),
    fetchHead: async () => {
      // The route's template, never the compat pointer.
      const state = await getStoreWebsiteEditorStateV3(client, storeId, templateId);
      if (state.head === null || state.draft === null) {
        throw new WebsiteTemplateServiceError('template_unavailable');
      }
      return { revisionId: state.head.revisionId, slots: state.draft.document.slots };
    },
    // The query client of the first caller: the entry outlives its screens.
    onSaved: () => {
      void invalidateWebsiteTemplateDraft(queryClient, storeId, templateId);
    },
    // Codes only: no slot values, no merchant text.
    log: (code, detail) => {
      console.error('[draftSaveQueue]', detail === undefined ? code : `${code}:${detail}`);
    },
  });
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden' && draftSaveHasWork(queue.getSnapshot())) {
      void queue.flush();
    }
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  const entry: RegistryEntry = {
    queue,
    subscribers: 0,
    release: () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unsubscribe();
    },
  };
  // A late result with no screen left: retire once the work is done.
  const unsubscribe = queue.subscribe(() => retireIfIdle(key, entry));
  return entry;
}

function getOrCreateEntry(
  key: string,
  args: UseTemplateDraftSaveQueueArgs,
  queryClient: QueryClient,
): RegistryEntry {
  const existing = registry.get(key);
  if (existing !== undefined) return existing;
  const created = createEntry(key, args, queryClient);
  registry.set(key, created);
  return created;
}

/**
 * Epic #228 P4: the auto-save queue of the template this screen edits, with
 * its live snapshot. Creating the entry during render is idempotent (the
 * registry decides), so a StrictMode double render yields one queue;
 * subscriber counting lives in effects, and the release check runs a
 * microtask later so StrictMode's unmount–remount keeps the entry.
 */
export function useTemplateDraftSaveQueue(
  args: UseTemplateDraftSaveQueueArgs,
): UseTemplateDraftSaveQueueResult {
  const queryClient = useQueryClient();
  const { client, storeId, templateId, headRevisionId } = args;
  const key = entryKey(storeId, templateId);
  const entry = getOrCreateEntry(key, args, queryClient);
  const { queue } = entry;
  const snapshot = useSyncExternalStore(queue.subscribe, queue.getSnapshot);
  const [, rerender] = useReducer((tick: number) => tick + 1, 0);

  useEffect(() => {
    const current = getOrCreateEntry(
      key,
      { client, storeId, templateId, headRevisionId },
      queryClient,
    );
    current.subscribers += 1;
    // A response landing between this commit and its effects can retire the
    // entry the render bound (no subscriber yet, no work left). Re-render so
    // the component reads the live entry and resubscribes to its queue.
    if (current !== entry) rerender();
    return () => {
      current.subscribers -= 1;
      queueMicrotask(() => retireIfIdle(key, current));
    };
    // The entry is keyed by store and template; the other args only seed a new one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, queryClient]);

  useEffect(() => {
    queue.observeHead(headRevisionId);
  }, [queue, headRevisionId]);

  return { queue, snapshot };
}
